import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import type { MargensPropostaQuedaLinha } from '@triade/contracts';
import { useMargensCustoPropostas } from '@/api/margens';
import { FaixaLucroBadge } from '@/components/profit-badge';
import { formatarContagem, formatarData, formatarFracaoPercentual, formatarMoeda, formatarPontos } from '@/lib/format';
import { Bloco } from '../bloco';
import { Paginacao } from '../paginacao';
import { TabelaOrdenavel, type ColunaMargem } from '../tabela-ordenavel';
import type { BuscaMargens } from '../tipos';
import { consultaCusto, DIRECAO_PROPOSTAS } from './tipos';

type Campo = keyof typeof DIRECAO_PROPOSTAS;

/** Propostas abertas (enviadas ou em negociacao, na validade) com queda de lucro alem do limiar. Tudo vem da API. */
export function SecaoPropostasQueda({ busca, limit = 10, ativo = true }: { busca: BuscaMargens; limit?: number; ativo?: boolean }) {
  const [ordenar, setOrdenar] = useState<Campo>('queda');
  const [offset, setOffset] = useState(0);
  const consulta = useMargensCustoPropostas({ ...consultaCusto(busca), ordenar, limit, offset }, ativo);
  const dados = consulta.data;

  const ordenarPor = (campo: string) => {
    setOffset(0);
    setOrdenar(campo as Campo);
  };

  const colunas: ColunaMargem<MargensPropostaQuedaLinha>[] = [
    {
      id: 'orcamento',
      rotulo: 'Orçamento',
      campo: 'numero',
      mono: true,
      celula: (p) => (
        <Link to="/vender/orcamentos/$id" params={{ id: p.orcamentoId }} data-testid="abrir-orcamento-custo" className="font-semibold text-ink no-underline hover:underline">
          {p.numeroExibido}
        </Link>
      ),
    },
    { id: 'cliente', rotulo: 'Cliente · tabela', celula: (p) => <span className="text-ink-secondary">{p.clienteNome ?? 'Sem cliente'} · {p.tabelaNome}</span> },
    {
      id: 'validade',
      rotulo: 'Válida até',
      campo: 'validade',
      mono: true,
      celula: (p) => <span className="text-ink-secondary">{formatarData(p.validaAte)}</span>,
    },
    {
      id: 'envio-hoje',
      rotulo: 'No envio → hoje',
      celula: (p) => (
        <span className="inline-flex flex-wrap items-center gap-1.5">
          <span className="tabular">{formatarFracaoPercentual(p.exibicao.noEnvio)}</span>
          <span aria-hidden="true">→</span>
          {p.faixaHoje ? <FaixaLucroBadge faixa={p.faixaHoje} /> : null}
          <span className="tabular">{formatarFracaoPercentual(p.exibicao.hoje)}</span>
          {p.itensSemCustoHoje > 0 && <span className="text-caption text-ink-muted">{p.itensSemCustoHoje} sem custo hoje</span>}
        </span>
      ),
    },
    {
      id: 'queda',
      rotulo: 'Queda',
      campo: 'queda',
      direita: true,
      mono: true,
      celula: (p) => <span className="font-semibold text-warning-ink">{formatarPontos(p.exibicao.quedaPp)} pp</span>,
    },
    { id: 'receita', rotulo: 'Total', direita: true, mono: true, celula: (p) => formatarMoeda(p.exibicao.receita) },
  ];

  return (
    <section aria-label="Propostas abertas com queda de lucro além do limiar" aria-busy={consulta.isFetching} className="rounded-2xl border border-border bg-card px-5 py-[18px]" data-testid="secao-custo-propostas">
      <div className="mb-3.5">
        <h2 className="m-0 text-title font-semibold">Propostas abertas com queda de lucro além do limiar</h2>
        <div className="text-small text-ink-muted">
          Enviadas ou em negociação, dentro da validade, com o lucro de hoje (custos atuais) abaixo do lucro do envio além do limiar da empresa.
          {dados?.limiarPp != null ? ` Limiar: ${formatarPontos(dados.limiarPp)} pp.` : ' Sem parâmetros vigentes: nenhum alerta.'}
        </div>
      </div>
      {dados && dados.foraDoLimite > 0 && (
        <p role="status" data-testid="aviso-fora-do-limite" className="mb-3 rounded-xl border border-border bg-line-soft px-3 py-2 text-small text-ink-body">
          {formatarContagem(dados.foraDoLimite)} {dados.foraDoLimite === 1 ? 'proposta ficou' : 'propostas ficaram'} de fora do cálculo: o painel calcula só as {formatarContagem(dados.maximoCalculado)} mais recentes. Restrinja o período para vê-las.
        </p>
      )}
      <Bloco
        carregando={consulta.isFetching}
        erro={consulta.error}
        temDados={!!dados}
        vazio={dados?.total === 0}
        mensagemVazia="Nenhuma proposta aberta com queda de lucro além do limiar nesses filtros."
        textoCarregando="Carregando propostas…"
        onTentar={() => void consulta.refetch()}
      >
        {dados && (
          <div className="flex flex-col gap-3">
            <TabelaOrdenavel
              legenda="Propostas com queda de lucro além do limiar"
              linhas={dados.itens}
              colunas={colunas}
              ordenar={ordenar}
              direcao={DIRECAO_PROPOSTAS[ordenar]}
              onOrdenar={ordenarPor}
              chave={(p) => p.orcamentoId}
              testIdLinha="linha-custo-proposta"
            />
            <Paginacao offset={dados.offset} limit={limit} quantidade={dados.itens.length} total={dados.total} onMudar={setOffset} testId="contagem-custo-propostas" />
          </div>
        )}
      </Bloco>
    </section>
  );
}
